import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from 'framer-motion';
import { api } from '@/services/api';
import { PunishmentMenu } from './PunishmentMenu';
import { UserDetailCard } from './UserDetailCard';
interface PendingItem {
    id: string;
    title: string;
    user: {
        id: string;
        display_name: string;
        icon_url: string;
        flags: number;
        status: string;
        created_at: number;
    };
    audio_url: string;
}

import { useToast } from '@/hooks/use-toast';
import { useSoundEffects } from '@/hooks/useSoundEffects';

export const SwipeModeration = () => {
    const { toast } = useToast();
    const [stamp, setStamp] = useState<'approved' | 'rejected' | null>(null);
    const [queue, setQueue] = useState<PendingItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isDetailOpen, setIsDetailOpen] = useState(false);

    // Physics
    const x = useMotionValue(0);
    const rotate = useTransform(x, [-200, 200], [-25, 25]);
    const opacity = useTransform(x, [-150, 0, 150], [0.5, 1, 0.5]);

    useEffect(() => {
        const fetchQueue = async () => {
            const res = await api.getAdminQueue();
            if (res.success) {
                setQueue(res.data);
            } else {
                toast({ title: "エラー", description: "キューの取得に失敗しました", variant: "destructive" });
            }
            setLoading(false);
        };
        fetchQueue();
    }, []);

    const { playApprove, playReject } = useSoundEffects();

    const handleSwipe = async (dir: 'left' | 'right' | 'up') => {
        if (!queue[0]) return;

        if (dir === 'up') {
            setIsMenuOpen(true);
            animate(x, 0);
            return;
        }

        if (dir === 'right') playApprove();
        else playReject();

        // Stamp Animation
        setStamp(dir === 'right' ? 'approved' : 'rejected');

        // Optimistic UI Update + API Call
        const targetId = queue[0].id;
        const action = dir === 'right' ? 'approve' : 'reject';

        // Fire and forget (or await if critical) - UX priority is speed.
        api.reviewSubmission(targetId, action).then(res => {
            if (!res.success) {
                console.error('Review failed', res);
                toast({ title: "エラー", description: "審査の送信に失敗しました", variant: "destructive" });
            }
        });

        await new Promise(r => setTimeout(r, 300));

        let exitX = 0;
        if (dir === 'left') exitX = -500;
        if (dir === 'right') exitX = 500;

        await animate(x, exitX, { duration: 0.2 });

        // Toast
        toast({
            title: dir === 'right' ? "承認しました" : "却下しました",
            description: `${queue[0].title} を${dir === 'right' ? '承認' : '却下'}しました。`,
            variant: dir === 'right' ? "default" : "destructive",
        });

        setQueue((prev) => prev.slice(1));
        x.set(0);
        setStamp(null);

        // Log
        console.log(`Swiped ${dir} on ${targetId}`);
    };

    const handlePunish = async (action: string, reason: string) => {
        if (!queue[0]) return;
        const targetId = queue[0].user.id;
        const result = await api.adminPunishUser(targetId, action, reason);

        if (result.success) {
            toast({
                title: "処罰適用完了",
                description: `ユーザー ${queue[0].user.display_name} に処罰 (${action}) を適用しました。`,
                variant: "destructive",
            });
        } else {
            toast({
                title: "エラー発生",
                description: `処罰の適用に失敗しました: ${result.error}`,
                variant: "destructive",
            });
        }

        setIsMenuOpen(false);
        setIsDetailOpen(false);
        handleSwipe('left');
    };

    if (loading) return <div>Loading...</div>;
    if (queue.length === 0) return <div>審査完了</div>;

    const current = queue[0];

    return (
        <div className="flex flex-col items-center justify-center p-4 w-full h-[600px] relative">
            <div className="relative w-full max-w-sm h-[500px]">
                <AnimatePresence>
                    <motion.div
                        key={current.id}
                        style={{ x, rotate, opacity }}
                        drag
                        dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
                        dragElastic={0.1}
                        onDragEnd={(_, info) => {
                            if (info.offset.y < -100) {
                                handleSwipe('up');
                            } else if (Math.abs(info.offset.x) > 150) {
                                handleSwipe(info.offset.x > 0 ? 'right' : 'left');
                            } else {
                                animate(x, 0);
                            }
                        }}
                        className="absolute inset-0 z-10 cursor-grab active:cursor-grabbing bg-surface border-2 border-ink p-4 rounded-xl flex flex-col justify-between"
                        onDoubleClick={() => setIsDetailOpen(true)}
                    >
                        {/* Stamp Animation */}
                        <AnimatePresence>
                            {stamp && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.5, rotate: -20 }}
                                    animate={{ opacity: 1, scale: 1.2, rotate: -15 }}
                                    exit={{ opacity: 0 }}
                                    className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 border-4 rounded-lg p-4 font-bold text-5xl uppercase tracking-widest z-50 pointer-events-none bg-cream-2 backdrop-blur-sm ${stamp === 'approved' ? 'border-green-500 text-green-500' : 'border-red-500 text-red-500'
                                        }`}
                                >
                                    {stamp === 'approved' ? 'APPROVED' : 'REJECTED'}
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <div className="text-center">
                            <h3 className="text-ink font-bold text-2xl mt-8">{current.title}</h3>
                            <p className="text-ink-2 mt-2">{current.user.display_name}</p>
                        </div>

                        <div className="mt-4 flex gap-2 w-full">
                            <Button onClick={() => handleSwipe('left')} variant="destructive" className="flex-1 h-14 bg-red-500/80">却下</Button>
                            <Button onClick={() => setIsDetailOpen(true)} variant="secondary" className="flex-1 h-14 bg-blue-500/80 text-ink font-bold border-2 border-ink" id="btn-details">詳細</Button>
                            <Button onClick={() => handleSwipe('right')} variant="default" className="flex-1 h-14 bg-green-500/80">承認</Button>
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>

            <PunishmentMenu open={isMenuOpen} onOpenChange={setIsMenuOpen} onSelect={handlePunish} />

            {isDetailOpen && (
                <div className="absolute inset-0 z-50 bg-ink/80 flex items-end">
                    <div className="w-full h-[90%] bg-surface rounded-t-xl overflow-hidden">
                        <UserDetailCard user={current.user} onPunish={() => setIsMenuOpen(true)} onClose={() => setIsDetailOpen(false)} />
                    </div>
                </div>
            )}
        </div>
    );
};
